/**
 * WebRTCClient — manages RTCPeerConnection for live streaming.
 * - SDP offer/answer exchange (via SignallingClient)
 * - ICE candidate exchange
 * - Remote stream handling (annotated video from server)
 * - Connection stats monitoring (getStats() for FPS, latency, packet loss)
 * - Graceful degradation: detect packet loss > 5%
 */

const ICE_SERVERS: RTCIceServer[] = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
]

const ICE_STUCK_TIMEOUT_MS = 8_000
const ICE_DISCONNECTED_TIMEOUT_MS = 5_000

export interface IceDiagnostics {
  /** ICE connection state transitions with timestamps (Date.now()) */
  stateHistory: Array<{ state: RTCIceConnectionState; ts: number }>
  /** Count of local ICE candidates by type */
  candidateCounts: { host: number; srflx: number; relay: number; unknown: number }
  /** Selected candidate pair types (from getStats after connect) */
  selectedPair: { localType: string; remoteType: string } | null
  /** Milliseconds from setRemoteDescription(answer) to first connected/completed */
  msToConnected: number | null
}

export interface WebRTCStats {
  fps: number
  latencyMs: number
  packetLossPct: number
}

export interface WebRTCClientOptions {
  onRemoteStream: (stream: MediaStream) => void
  onFirstFrame: () => void
  onStats: (stats: WebRTCStats) => void
  onDegraded: () => void
  onIceCandidate: (candidate: RTCIceCandidateInit) => void
  onIceConnectionFailed: () => void
  iceServers?: RTCIceServer[]
  /** Called after PC creation but before setLocalDescription, to create data channels */
  onPeerConnectionReady?: (pc: RTCPeerConnection) => void
  /** Called when ICE stays in checking/new past ICE_STUCK_TIMEOUT_MS after answer received */
  onIceStuck?: (diagnostics: IceDiagnostics) => void
  /** Called on every iceConnectionState change */
  onIceStateChange?: (state: RTCIceConnectionState) => void
  /** Passed to RTCPeerConnection iceTransportPolicy config */
  iceTransportPolicy?: 'all' | 'relay'
}

interface PrevStatsSnapshot {
  packetsReceived: number
  framesDecoded: number
  timestamp: number
}

export class WebRTCClient {
  private options: WebRTCClientOptions
  private pc: RTCPeerConnection | null = null
  private statsTimer: ReturnType<typeof setInterval> | null = null
  private firstFrameReceived = false
  private prevStats: PrevStatsSnapshot | null = null
  private iceStuckTimer: ReturnType<typeof setTimeout> | null = null
  private iceDisconnectedTimer: ReturnType<typeof setTimeout> | null = null
  private answerReceivedAt: number | null = null
  private iceDiag: IceDiagnostics = {
    stateHistory: [],
    candidateCounts: { host: 0, srflx: 0, relay: 0, unknown: 0 },
    selectedPair: null,
    msToConnected: null,
  }

  constructor(options: WebRTCClientOptions) {
    this.options = options
  }

  /**
   * PRI-496 Lane B: adopt ICE servers delivered AFTER construction. The public
   * streams plane returns ice_servers on the create response (no separate
   * turn-credentials fetch); the peer connection reads them at createOffer
   * time, so setting before the `ready` signal is race-free.
   */
  setIceServers(servers: RTCIceServer[]): void {
    this.options.iceServers = servers
  }

  async createOffer(localStream: MediaStream): Promise<string> {
    this.pc = new RTCPeerConnection({
      iceServers: this.options.iceServers ?? ICE_SERVERS,
      iceTransportPolicy: this.options.iceTransportPolicy ?? 'all',
    })

    for (const track of localStream.getVideoTracks()) {
      const sender = this.pc.addTrack(track, localStream)
      // Prefer H.264 for both send and receive directions.
      // IMPORTANT: setCodecPreferences must include codecs from BOTH sender AND
      // receiver capabilities. Using only RTCRtpSender.getCapabilities() omits
      // H.264 from the receive direction in the SDP, causing the server's outbound
      // H.264 track to fail with "codec is not supported by remote".
      try {
        const sendCaps = RTCRtpSender.getCapabilities('video')
        const recvCaps = RTCRtpReceiver.getCapabilities('video')
        // Merge send + receive codecs, deduplicated by mimeType+sdpFmtpLine
        const allCodecs: RTCRtpCodec[] = []
        const seen = new Set<string>()
        for (const codec of [...(sendCaps?.codecs ?? []), ...(recvCaps?.codecs ?? [])]) {
          const key = `${codec.mimeType}|${codec.sdpFmtpLine ?? ''}`
          if (!seen.has(key)) { seen.add(key); allCodecs.push(codec) }
        }
        const h264 = allCodecs.filter(c => c.mimeType.toLowerCase() === 'video/h264')
        const rest = allCodecs.filter(c => c.mimeType.toLowerCase() !== 'video/h264')
        if (h264.length > 0) {
          const transceiver = this.pc!.getTransceivers().find(t => t.sender === sender)
          transceiver?.setCodecPreferences([...h264, ...rest])
        }
      } catch {
        // setCodecPreferences not supported (older browser) — fall back to default
      }
    }

    this.pc.ontrack = (event) => {
      if (event.streams[0]) {
        this.options.onRemoteStream(event.streams[0])
        // Fire onFirstFrame immediately when the remote track arrives.
        // The original fps>0 path is unreliable: Safari doesn't report framesDecoded
        // unless the stream is attached to a <video> element, and the server may not
        // return encoded video frames at all (results come via signalling metadata).
        // Transition to 'live' as soon as we have a remote track.
        if (!this.firstFrameReceived) {
          this.firstFrameReceived = true
          this.options.onFirstFrame()
        }
        this.startStatsMonitoring()
      }
    }

    this.pc.onicecandidate = (event) => {
      if (event.candidate) {
        const raw = event.candidate.candidate ?? ''
        if (raw.includes('typ relay')) this.iceDiag.candidateCounts.relay++
        else if (raw.includes('typ srflx')) this.iceDiag.candidateCounts.srflx++
        else if (raw.includes('typ host')) this.iceDiag.candidateCounts.host++
        else this.iceDiag.candidateCounts.unknown++
        this.options.onIceCandidate(event.candidate.toJSON())
      }
    }

    this.pc.oniceconnectionstatechange = () => {
      const state = this.pc?.iceConnectionState
      if (!state) return
      const ts = Date.now()
      this.iceDiag.stateHistory.push({ state, ts })
      console.log('[Stream] ICE connection state:', state)
      this.options.onIceStateChange?.(state)

      if (state === 'connected' || state === 'completed') {
        this._clearIceStuckTimer()
        this._clearDisconnectedTimer()
        if (this.answerReceivedAt !== null && this.iceDiag.msToConnected === null) {
          this.iceDiag.msToConnected = ts - this.answerReceivedAt
        }
        // Extract selected candidate pair from stats (best-effort, async, non-blocking)
        void this.pc?.getStats().then(statsReport => {
          statsReport.forEach(report => {
            if (
              report.type === 'candidate-pair' &&
              ((report as { nominated?: boolean }).nominated ||
               (report as { state?: string }).state === 'succeeded')
            ) {
              const r = report as { localCandidateId?: string; remoteCandidateId?: string }
              const localCand = r.localCandidateId
                ? statsReport.get(r.localCandidateId) as { candidateType?: string } | undefined
                : undefined
              const remoteCand = r.remoteCandidateId
                ? statsReport.get(r.remoteCandidateId) as { candidateType?: string } | undefined
                : undefined
              if (localCand || remoteCand) {
                this.iceDiag.selectedPair = {
                  localType: localCand?.candidateType ?? 'unknown',
                  remoteType: remoteCand?.candidateType ?? 'unknown',
                }
              }
            }
          })
        }).catch(() => {})
      } else if (state === 'failed') {
        this._clearIceStuckTimer()
        this._clearDisconnectedTimer()
        this.options.onIceConnectionFailed()
      } else if (state === 'disconnected') {
        this._clearDisconnectedTimer()
        this.iceDisconnectedTimer = setTimeout(() => {
          this.iceDisconnectedTimer = null
          if (this.pc?.iceConnectionState === 'disconnected') {
            console.warn('[Stream] ICE disconnected for 5s without recovery — treating as failed')
            this.options.onIceConnectionFailed()
          }
        }, ICE_DISCONNECTED_TIMEOUT_MS)
      } else if (state === 'closed') {
        this._clearIceStuckTimer()
        this._clearDisconnectedTimer()
      }
    }

    // Allow caller to create data channels before the offer
    this.options.onPeerConnectionReady?.(this.pc)

    const offer = await this.pc.createOffer({
      offerToReceiveVideo: true,
      offerToReceiveAudio: false,
    })

    await this.pc.setLocalDescription(offer)
    return offer.sdp ?? ''
  }

  async handleAnswer(sdp: string): Promise<void> {
    if (!this.pc) return
    await this.pc.setRemoteDescription(new RTCSessionDescription({ type: 'answer', sdp }))
    this.answerReceivedAt = Date.now()
    this._clearIceStuckTimer()
    this.iceStuckTimer = setTimeout(() => {
      this.iceStuckTimer = null
      const state = this.pc?.iceConnectionState
      if (state === 'checking' || state === 'new') {
        console.warn('[Stream] ICE stuck watchdog fired — state=%s after %dms', state, ICE_STUCK_TIMEOUT_MS)
        this.options.onIceStuck?.(this.getIceDiagnostics())
      }
    }, ICE_STUCK_TIMEOUT_MS)
  }

  async addIceCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!this.pc) return
    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate))
    } catch {
      // Ignore stale candidates after connection is established
    }
  }

  private startStatsMonitoring(): void {
    this.statsTimer = setInterval(async () => {
      if (!this.pc) return
      const statsReport = await this.pc.getStats()
      let fps = 0
      let latencyMs = 0
      let packetLossPct = 0

      statsReport.forEach((report) => {
        if (report.type === 'inbound-rtp' && (report as RTCStatsReport & { kind?: string }).kind === 'video') {
          const r = report as RTCStatsReport & {
            framesDecoded?: number
            packetsReceived?: number
            packetsLost?: number
          }
          const framesDecoded = r.framesDecoded ?? 0
          const packetsReceived = r.packetsReceived ?? 0
          const packetsLost = r.packetsLost ?? 0
          const now = Date.now()

          if (this.prevStats) {
            const dt = (now - this.prevStats.timestamp) / 1000
            if (dt > 0) {
              fps = Math.round((framesDecoded - this.prevStats.framesDecoded) / dt)
            }
            const totalPackets = (packetsReceived - this.prevStats.packetsReceived) + packetsLost
            packetLossPct = totalPackets > 0 ? (packetsLost / totalPackets) * 100 : 0

            // fps>0 path kept for completeness but onFirstFrame is already fired
            // from ontrack (see above), so this is a no-op in practice.
            if (!this.firstFrameReceived && fps > 0) {
              this.firstFrameReceived = true
              this.options.onFirstFrame()
            }

            if (packetLossPct > 5) {
              this.options.onDegraded()
            }
          }

          this.prevStats = { packetsReceived, framesDecoded, timestamp: now }
        }

        if (report.type === 'candidate-pair') {
          const r = report as RTCStatsReport & { state?: string; currentRoundTripTime?: number }
          if (r.state === 'succeeded' && r.currentRoundTripTime != null) {
            latencyMs = Math.round(r.currentRoundTripTime * 1000)
          }
        }
      })

      this.options.onStats({ fps, latencyMs, packetLossPct })
    }, 2000)
  }

  getIceDiagnostics(): IceDiagnostics {
    return {
      stateHistory: [...this.iceDiag.stateHistory],
      candidateCounts: { ...this.iceDiag.candidateCounts },
      selectedPair: this.iceDiag.selectedPair ? { ...this.iceDiag.selectedPair } : null,
      msToConnected: this.iceDiag.msToConnected,
    }
  }

  private _clearIceStuckTimer(): void {
    if (this.iceStuckTimer) { clearTimeout(this.iceStuckTimer); this.iceStuckTimer = null }
  }

  private _clearDisconnectedTimer(): void {
    if (this.iceDisconnectedTimer) { clearTimeout(this.iceDisconnectedTimer); this.iceDisconnectedTimer = null }
  }

  close(): void {
    this._clearIceStuckTimer()
    this._clearDisconnectedTimer()
    if (this.statsTimer) { clearInterval(this.statsTimer); this.statsTimer = null }
    if (this.pc) {
      this.pc.close()
      this.pc = null
    }
    this.firstFrameReceived = false
    this.prevStats = null
  }
}
